import NP from 'number-precision';
import { select } from 'd3-selection';
import { getTextWidth } from '../../../../helper/getTextWidth';

class TargetLine {
  constructor() {
    this.verticalLine = [];
    this.horizontalLine = [];
    this.arrowLine = [];
  }

  setValue({ plotId, verticalLine, horizontalLine, getXValue, getYValue, height, width, yMax, yMin, xMax, xMin, horizontalLineSetting, arrowLine, verticalLineSetting }) {
    this.plotId = plotId || this.plotId;
    this.verticalLine = verticalLine || this.verticalLine;
    this.horizontalLine = horizontalLine || this.horizontalLine;
    this.height = height || this.height;
    this.width = width || this.width;
    this.getXValue = getXValue || this.getXValue;
    this.getYValue = getYValue || this.getYValue;
    this.xMin = typeof xMin === 'number' ? xMin : this.xMin;
    this.xMax = typeof xMax === 'number' ? xMax : this.xMax;
    this.yMin = typeof yMin === 'number' ? yMin : this.yMin;
    this.yMax = typeof yMax === 'number' ? yMax : this.yMax;
    this.horizontalLineSetting = horizontalLineSetting || this.horizontalLineSetting;
    this.arrowLine = arrowLine || this.arrowLine;
    this.verticalLineSetting = verticalLineSetting || this.verticalLineSetting;
  }

  drawLines(root, drawType) {
    if (select(`#${this.plotId}-target-line`).empty()) {
      this.lineRoot = root.append('g')
        .attr('id', `${this.plotId}-target-line`);
    }

    switch (drawType) {
      case 'verticalLine':
        this.drawVerticalLine();
        break;
      case 'horizontalLine':
        this.drawHorizontalLine();
        break;
      case 'arrowLine':
        this.drawArrowLine();
        break;
      default:
        this.drawVerticalLine();
        this.drawHorizontalLine();
        this.drawArrowLine();
    }
  }

  drawVerticalLine() {
    this.removeLine(`${this.plotId}-vertical-line`)
    const xRoot = this.lineRoot.append('g')
      .attr('id', `${this.plotId}-vertical-line`);

    for (let index = 0; index < this.verticalLine.length; index++) {
      const _item = this.verticalLine[index];
      const item = Number(_item);
      const realX = NP.strip(NP.times(item, 1e9));
      const textWidth = getTextWidth(item, 12, null, 500);
      if (realX >= this.xMin && realX <= this.xMax) {
        let color = '#7a4816';
        let y2 = this.height;
        let y1 = 0;
        let text = item;
        if (this.verticalLineSetting && this.verticalLineSetting[index]) {
          color = this.verticalLineSetting[index].color || '#7a4816';
          y1 = this.verticalLineSetting[index].y1 ? this.getYValue(this.verticalLineSetting[index].y1) : 0;
          y2 = this.verticalLineSetting[index].y2 ? this.getYValue(this.verticalLineSetting[index].y2) : this.height;
          text = this.verticalLineSetting[index].unit ? `${item} ${this.verticalLineSetting[index].unit}` : item;
        }
        if (y1 > this.height) {
          return;
        }
        xRoot.append("line")
          .attr('x1', this.getXValue(realX))
          .attr('y1', y1)
          .attr('x2', this.getXValue(realX))
          .attr('y2', y2)
          .attr('stroke', color)
          .attr('stroke-width', 1.5)
          .attr('stroke-dasharray', "3,3");
        xRoot.append('text')
          .text(text)
          .attr('x', this.getXValue(realX) - textWidth / 2)
          .attr('y', -6)
          .attr('font-size', '12px')
          .attr('font-weight', 500)
          .attr('fill', color);
      }
    }
  }

  drawHorizontalLine() {
    this.removeLine(`${this.plotId}-horizontal-line`)
    const yRoot = this.lineRoot.append('g')
      .attr('id', `${this.plotId}-horizontal-line`);

    for (let index = 0; index < this.horizontalLine.length; index++) {
      const _item = this.horizontalLine[index];
      const item = Number(_item);
      if (item >= this.yMin && item <= this.yMax) {
        let color = '#7a4816', text = item, textXMove = 4, textYMove = 4, isDashed = true, lineXMove = 4, strokeWidth = 1.5;
        if (this.horizontalLineSetting && this.horizontalLineSetting[index]) {
          color = this.horizontalLineSetting[index].color || '#7a4816';
          text = this.horizontalLineSetting[index].text || item;
          textXMove = this.horizontalLineSetting[index].textXMove || 4;
          textYMove = this.horizontalLineSetting[index].textYMove || 4;
          isDashed = this.horizontalLineSetting[index].isDashed !== undefined ? this.horizontalLineSetting[index].isDashed : true;
          lineXMove = this.horizontalLineSetting[index].lineXMove === undefined ? 4 : this.horizontalLineSetting[index].lineXMove;
          strokeWidth = this.horizontalLineSetting[index].strokeWidth || 1.5;
        }
        const dashArray = isDashed ? "3,3" : null;
        yRoot.append('line')
          .attr('x1', 0)
          .attr('y1', this.getYValue(item))
          .attr('x2', this.width + lineXMove)
          .attr('y2', this.getYValue(item))
          .attr('stroke', color)
          .attr('stroke-width', strokeWidth)
          .attr('stroke-dasharray', dashArray);
        yRoot.append('text')
          .text(text)
          .attr('x', this.width + textXMove)
          .attr('y', this.getYValue(item) + textYMove)
          .attr('font-size', '12px')
          .attr('font-weight', 500)
          .attr('fill', color);
      }
    }
  }

  drawArrowLine() {
    this.removeLine(`${this.plotId}-arrow-line`)
    const arrowRoot = this.lineRoot.append('g')
      .attr('id', `${this.plotId}-arrow-line`);

    if (!this.arrowLine || !this.arrowLine.length) {
      return;
    }

    const defs = arrowRoot.append("defs");
    for (const item of this.arrowLine) {
      const { x, y, text, color, id } = item;
      // Render points within the axis
      if (x[0] >= this.xMin && x[0] <= this.xMax && x[1] >= this.xMin && x[1] <= this.xMax
        && y[0] >= this.yMin && y[0] <= this.yMax && y[1] >= this.yMin && y[1] <= this.yMax) {
        // double sided arrow
        const arrowMarker = defs.append("marker")
          .attr("id", `arrow-${id}`)
          .attr("markerUnits", "strokeWidth")
          .attr("markerWidth", "7")
          .attr("markerHeight", "7")
          .attr("viewBox", "0 0 12 12")
          .attr('refX', '6')
          .attr('refY', '6')
          .attr("orient", "auto");

        arrowMarker.append("path")
          .attr("d", "M2,2 L10,6 L2,10 L6,6 L2,2")
          .attr("fill", color);

        // reverse arrow
        const reverseArrowMarker = defs.append("marker")
          .attr("id", `arrow-reversed-${id}`)
          .attr("markerUnits", "strokeWidth")
          .attr("markerWidth", "7")
          .attr("markerHeight", "7")
          .attr("viewBox", "0 0 12 12")
          .attr('refX', '6')
          .attr('refY', '6')
          .attr("orient", "auto");

        reverseArrowMarker.append("path")
          .attr("d", "M10,2 L2,6 L10,10 L6,6 L10,2")
          .attr("fill", color);
        // segment
        arrowRoot.append("line")
          .attr("x1", this.getXValue(x[0]) + 4)
          .attr("y1", this.getYValue(y[0]))
          .attr("x2", this.getXValue(x[1]) - 4)
          .attr("y2", this.getYValue(y[1]))
          .attr("stroke", color)
          .attr("stroke-width", 2)
          .attr('stroke-dasharray', "3,3")
          .attr("marker-start", `url(#arrow-reversed-${id})`)
          .attr("marker-end", `url(#arrow-${id})`);

        // text center
        const textWidth = getTextWidth(text, 12, null, 500);
        arrowRoot.append("text")
          .text(text)
          .attr("x", (this.getXValue(x[0]) + this.getXValue(x[1]) - textWidth) / 2)
          .attr("y", this.getYValue(y[0]) + 20)
          .attr('font-size', '12px')
          .attr('font-weight', 500)
          .attr("fill", color)
      }
    }
  }

  removeLine(id) {
    if (id && !select(`#${id}`).empty()) {
      select(`#${id}`).remove();
    }
  }
}

export default TargetLine;